import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API_BASE = `${BACKEND_URL}/api`;

// External public data source (read-only): existing DAMS rental-routes admin
export const PUBLIC_API_BASE = "https://rental-routes.emergent.host/api";

// Contact info (single source of truth)
export const CONTACT = {
  whatsapp: "+1 849 919 7111",
  whatsappLink: "https://wa.me/18499197111",
  email: "info@damsrentacar.com",
  phone: "+1 849 919 7111",
  phoneLink: "tel:+18499197111",
};

// Android app download (served by backend with proper APK headers)
export const ANDROID_APP = {
  apkUrl: `${API_BASE}/download/android`,
  version: "v1.1.0",
};

// iOS app download
export const IOS_APP = {
  appStoreUrl: "https://apps.apple.com/us/app/damscarrental/id6771194657",
};

const api = axios.create({ baseURL: API_BASE });
const publicApi = axios.create({ baseURL: PUBLIC_API_BASE });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("dams_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err?.response?.status === 401) {
      if (!String(err.config?.url || "").includes("/auth/login")) {
        localStorage.removeItem("dams_token");
      }
      err.message = err?.response?.data?.detail || "Session expired. Please sign in again.";
    }
    return Promise.reject(err);
  }
);

// ---------- AUTH (admin only, our backend) ----------
export const authApi = {
  login: (email, password) =>
    api.post("/auth/login", { email, password }).then((r) => r.data),
};

// ---------- ADMIN INTERNAL APIs (our backend) ----------
const adminCarNormalize = (c) => {
  if (!c) return c;
  return {
    ...c,
    unlimitedMileage: c.unlimited_mileage ?? c.unlimitedMileage,
    mileageCap: c.mileage_cap ?? c.mileageCap,
    minDriverAge: c.min_driver_age ?? c.minDriverAge,
    pickupLocation: c.pickup_location ?? c.pickupLocation,
    dropoffLocation: c.dropoff_location ?? c.dropoffLocation,
  };
};
const adminLocNormalize = (l) =>
  l && { ...l, minBookingDays: l.min_booking_days ?? l.minBookingDays };

export const carsApi = {
  list: (params = {}) =>
    api.get("/cars", { params }).then((r) => (r.data || []).map(adminCarNormalize)),
  get: (id) => api.get(`/cars/${id}`).then((r) => adminCarNormalize(r.data)),
  create: (data) => api.post("/cars", data).then((r) => adminCarNormalize(r.data)),
  update: (id, data) => api.put(`/cars/${id}`, data).then((r) => adminCarNormalize(r.data)),
  remove: (id) => api.delete(`/cars/${id}`).then((r) => r.data),
};

export const locationsApi = {
  list: () => api.get("/locations").then((r) => (r.data || []).map(adminLocNormalize)),
  create: (data) => api.post("/locations", data).then((r) => adminLocNormalize(r.data)),
  update: (id, data) =>
    api.put(`/locations/${id}`, data).then((r) => adminLocNormalize(r.data)),
  remove: (id) => api.delete(`/locations/${id}`).then((r) => r.data),
};

// ---------- BOOKINGS (our backend — admin + public create) ----------
export const bookingsApi = {
  list: (params = {}) => api.get("/bookings", { params }).then((r) => r.data),
  byRef: (ref) => api.get(`/bookings/by-ref/${ref}`).then((r) => r.data),
  create: (data) => api.post("/bookings", data).then((r) => r.data),
  updateStatus: (id, status) =>
    api.patch(`/bookings/${id}/status`, { status }).then((r) => r.data),
  updatePayment: (id, paid) =>
    api.patch(`/bookings/${id}/payment`, { paid }).then((r) => r.data),
  remove: (id) => api.delete(`/bookings/${id}`).then((r) => r.data),
};

// ---------- STATS / MIGRATION (our admin) ----------
export const statsApi = { get: () => api.get("/stats").then((r) => r.data) };

export const migrationApi = {
  exportAll: () => api.get("/migration/export").then((r) => r.data),
  importAll: (payload) => api.post("/migration/import", payload).then((r) => r.data),
  recomputeTaxes: () => api.post("/migration/recompute-taxes").then((r) => r.data),
  normalizeImages: () => api.post("/migration/normalize-images").then((r) => r.data),
};

// ===================================================================
// PUBLIC CUSTOMER-FACING APIs — sourced from rental-routes (read-only)
// ===================================================================

// Map rental-routes car shape → customer site car shape
const isObjectId = (s) => typeof s === "string" && /^[a-f0-9]{24}$/i.test(s);

const resolveLocationName = (loc) => {
  if (!loc) return "";
  if (typeof loc === "string") return loc;
  // rental-routes has TWO formats for pickup_location:
  //   Format A (legacy): { name: "Santo Domingo Downtown", lat: 18.47, lng: -69.88 }
  //   Format B (newer):  { name: "<objectId>",            lat: "Santo Domingo", lng: 18.47 }
  if (isObjectId(loc.name) && typeof loc.lat === "string") return loc.lat;
  return loc.name || loc.city || "";
};

const featuresFromCar = (c) => {
  if (Array.isArray(c.features) && c.features.length > 0) return c.features;
  const list = [];
  if (c.android_auto) list.push("Android Auto");
  if (c.apple_carplay) list.push("Apple CarPlay");
  if (c.blind_spot_warning) list.push("Blind spot warning");
  if (c.gps) list.push("GPS");
  if (c.keyless_entry) list.push("Keyless entry");
  if (c.sunroof) list.push("Sunroof");
  return list;
};

const publicCarToCustomer = (c) => {
  if (!c) return c;
  const id = c.id || c._id || c.car_id;
  const fallbackImg = c.image_url || c.image || "";
  const arrImgs = Array.isArray(c.images) ? c.images.filter(Boolean) : [];
  const images = arrImgs.length > 0 ? arrImgs : (fallbackImg ? [fallbackImg] : []);
  const image = images[0] || fallbackImg;
  const locStr =
    resolveLocationName(c.pickup_location) ||
    c.location ||
    c.city ||
    "";
  const dropoffStr = resolveLocationName(c.dropoff_location) || locStr;

  return {
    id,
    name: c.name,
    brand: c.brand,
    model: c.model,
    year: c.year,
    category: c.category,
    price: c.price_per_day ?? c.price ?? 0,
    seats: c.seats ?? 5,
    bags: c.bags ?? 2,
    transmission: c.transmission || "Automatic",
    fuel: c.fuel_type || c.fuel || "Gasoline",
    description: c.description || "",
    features: featuresFromCar(c),
    // Mileage policy — preserve both legacy and snake_case so downstream components can read either.
    unlimitedMileage: c.unlimited_mileage ?? true,
    unlimited_mileage: c.unlimited_mileage,
    mileage_limit: c.mileage_limit ?? null,
    mileageCap: c.mileage_limit ?? c.mileage_cap ?? c.daily_mileage_cap ?? "",
    // Refundable security deposit (snake_case used by booking flow components)
    deposit: c.deposit ?? 0,
    minDriverAge: c.min_driver_age ?? c.minimum_driver_age ?? 21,
    pickupLocation: locStr,
    dropoffLocation: dropoffStr,
    location: locStr,
    image,
    images,
  };
};

const publicLocToCustomer = (l) => {
  if (!l) return l;
  const id = l.id || l._id;
  return {
    id,
    name: l.name,
    address: l.address || l.city || "",
    city: l.city || "",
    country: l.country || "",
    lat: l.lat ?? 0,
    lng: l.lng ?? 0,
    tax: l.tax_rate ?? l.tax ?? 0,
    minBookingDays: l.min_booking_days ?? 1,
    type: l.type || "both",
  };
};

export const publicCarsApi = {
  list: async (params = {}) => {
    const r = await publicApi.get("/cars", { params });
    return (r.data || []).map(publicCarToCustomer);
  },
  // rental-routes has /api/cars/all (no filters) — prefer this for full inventory
  listAll: async () => {
    try {
      const r = await publicApi.get("/cars/all");
      return (r.data || []).map(publicCarToCustomer);
    } catch {
      const r = await publicApi.get("/cars");
      return (r.data || []).map(publicCarToCustomer);
    }
  },
  get: async (id) => {
    try {
      const r = await publicApi.get(`/cars/${id}`);
      return publicCarToCustomer(r.data);
    } catch {
      // Fallback: fetch all and find
      const all = await publicCarsApi.listAll();
      return all.find((c) => c.id === id) || null;
    }
  },
};

export const publicLocationsApi = {
  list: async () => {
    const r = await publicApi.get("/locations");
    return (r.data || []).map(publicLocToCustomer);
  },
  taxByName: async (name) => {
    try {
      const r = await publicApi.get(`/locations/tax-by-name`, { params: { name } });
      return {
        tax: r.data?.tax_rate ?? 0,
        minBookingDays: r.data?.min_booking_days ?? 1,
        name: r.data?.name,
      };
    } catch {
      return { tax: 0, minBookingDays: 1, name };
    }
  },
};

export default api;
